import { useState } from "react";
import { useIntl } from "react-intl";

import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  apiShape,
  backendShape,
  useBrowser,
  useNotifier,
  useRules,
  withApi,
  withBackend,
} from "../../context";
import { ActionBar, ActionBarButtons, ActionButton } from "../ActionBar";
import Heading from "../Heading";

import { BadRequestError, PaymentRequiredError } from "../../api";
import ProductRules from "../../rules/ProductRules";
import {
  convertLabelToSpecificationKey,
  createEnforcedTenant,
  getProductCodeForRules,
  isNotEmpty,
  isSomething,
} from "../../utils";
import { Column, Container, Row } from "../Grid";
import AssetForm from "./AssetForm";

import SpecificationKeys from "../../constants/SpecificationKeys";
import { isUserInGroup } from "../../extractors";
import { useUser } from "../../hooks";
import * as gpsUtils from "../../utils/gpsUtils";
import BackButton from "../BackButton";
import { PROJECT_ENGINEER_ROLE } from "../Specifications/SpecificationConstants";
import AssetPermissionModal from "./AssetPermissionModal";
import ConnectedAssetSubscriptionClue from "./ConnectedAssetSubscriptionClue";

type Props = {
  readonly api: apiShape;
  readonly backend: backendShape;
};

export function AssetCreate({ api, backend }: Props) {
  const intl = useIntl();
  const rules = useRules();
  const browser = useBrowser();
  const notifier = useNotifier();
  const user = useUser();
  const params = useParams<{
    id?: string;
    serialNumber?: string;
  }>();
  const queryClient = useQueryClient();

  const [formValues, setFormValues] = useState();
  const [subscriptionValid, setSubscriptionValid] = useState(false);
  const [askPermission, setAskPermission] = useState(false);
  const [askPermissionSerialNumber, setAskPermissionSerialNumber] =
    useState(null);
  const [askPermissionManufacturer, setAskPermissionManufacturer] =
    useState(null);
  const [askPermissionProduct, setAskPermissionProduct] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const initialValues = {
    serial_number: params?.serialNumber,
  };

  const isUserProjectEngineer = isUserInGroup(user, PROJECT_ENGINEER_ROLE);

  const createNewTenant = async (tenant: $TSFixMe, manufacturer: $TSFixMe) => {
    let newTenant = null;
    // New manufacturer, user has no tenant yet
    if (!tenant && manufacturer.new) {
      newTenant = {
        id: (
          await createEnforcedTenant(
            api,
            [user?.firstName, user?.lastName].join(" ").trim(),
          )
        ).id,
      };
      // Existing private manufacturer
    } else if (!manufacturer.new && !manufacturer.tenantPublic) {
      newTenant = { id: manufacturer.tenantId };
      // Create completely new tenant
    } else if (tenant?.new) {
      newTenant = {
        id: (await api.post("/tenants", { name: tenant.name })).id,
      };
    }
    if (newTenant) {
      return newTenant;
    }
    return tenant;
  };

  const createNewProduct = async (
    product: $TSFixMe,
    manufacturer: $TSFixMe,
    tenant: $TSFixMe,
    values: $TSFixMe,
  ) => {
    let newProduct;
    if (product.new) {
      newProduct = await api.post("/products", {
        manufacturer: { id: manufacturer.id },
        name: product.name,
        product_code: product.code,
        tenant: { id: tenant.id },
      });

      // assign category to new product if needed
      /* istanbul ignore next */
      if (values.product_category) {
        await api.post(`/products/${newProduct.id}/categories`, {
          categories: [
            {
              id: values.product_category.id,
            },
          ],
        });
      }
    }
    if (newProduct) {
      return newProduct;
    }
    return product;
  };

  const handleNotUniqueError = async (
    error: $TSFixMe,
    actions: $TSFixMe,
    values: $TSFixMe,
  ) => {
    const errorMessage = error.extractError("not_unique_in_scope").message;
    const includesManufacturer = errorMessage.includes("manufacturer");
    const scope = includesManufacturer ? "manufacturer" : "product";

    const messageId = `api.error.serial_number.not_unique_in_scope_for_${scope}`;
    const assetRequestUrl = includesManufacturer
      ? `/assets?serial_number=${values.serial_number}&manufacturer_id=${values.manufacturer.id}`
      : `/assets?serial_number=${values.serial_number}&product_id=${values.product.id}`;

    actions.setErrors({
      serial_number: intl.formatMessage({ id: messageId }),
    });

    const response = await api.get(assetRequestUrl);

    if (response.assets.length === 0) {
      setAskPermission(true);
      setAskPermissionSerialNumber(values.serial_number);
      setAskPermissionProduct(values.product);
      setAskPermissionManufacturer(values.manufacturer);
    }
  };

  const handleError = async (
    error: $TSFixMe,
    actions: $TSFixMe,
    values: $TSFixMe,
  ) => {
    if (
      error instanceof BadRequestError &&
      // @ts-expect-error TS(2345) FIXME: Argument of type '"serial_number"' is not assignab... Remove this comment to see the full error message
      error.contains("not_unique_in_scope", "serial_number")
    ) {
      await handleNotUniqueError(error, actions, values);
    } else if (
      error instanceof PaymentRequiredError &&
      error.contains("quota_exceeded")
    ) {
      setQuotaExceeded(true);
    } else {
      notifier.showError(api.translateError(error));
    }
  };

  const getUrlToNavigate = (
    proceedWithTag: $TSFixMe,
    needsInstrumentation: $TSFixMe,
    response: $TSFixMe,
  ) => {
    let url = "";
    let endpoint = "";
    if (proceedWithTag || needsInstrumentation) {
      if (needsInstrumentation) {
        endpoint = "/instrumentations/create";
      } else {
        endpoint = "/instrumentations/assign";
      }
      if (params.id) {
        url = `/nodes/${params.id}/assets/${response.id}${endpoint}`;
      } else {
        url = `/assets/${response.id}${endpoint}`;
      }
    } else if (params?.id) {
      url = `/nodes/${params.id}`;
    } else {
      url = `/assets/${response.id}`;
    }
    return url;
  };

  const setSpecifications = (
    productRules: $TSFixMe,
    values: $TSFixMe,
    manufacturer: $TSFixMe,
    productNameForRule: string,
  ) => {
    const showMeasuringInterval = productRules?.get("needsMeasuringInterval");
    const showTransmissionInterval = productRules?.get(
      "needsTransmissionInterval",
    );
    const showTimeOfFirstMeasurement = productRules?.get(
      "needsTimeOfFirstMeasurement",
    );
    const isEndress = manufacturer?.name === "Endress+Hauser";

    const specifications: Record<
      string,
      {
        value: string;
        unit?: string;
        source_timestamp?: string;
        ui_visible: boolean;
      }
    > = {};

    if (showTransmissionInterval) {
      specifications[
        // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
        [SpecificationKeys.ConnectivitySettings.TRANSMISSION_INTERVAL]
      ] = {
        value: values.transmissionInterval.id,
      };
    }

    if (showMeasuringInterval) {
      specifications[
        // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
        [SpecificationKeys.ConnectivitySettings.MEASURING_INTERVAL]
      ] = {
        value: values.measuringInterval.id,
      };
    }

    if (showTimeOfFirstMeasurement && isSomething(values.firstMeasurement)) {
      specifications[
        // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
        [SpecificationKeys.ConnectivitySettings.FIRST_MEASUREMENT]
      ] = {
        value: values.firstMeasurement.toISOString(),
      };
    }

    const basicSettings =
      showMeasuringInterval &&
      productRules.get("needsGPSInterval") &&
      isSomething(values.transmissionInterval);
    const enableGPSSettings = productRules.get(
      "enableGPSForOrderCode",
      basicSettings,
      values.extendedOrderCode,
      false,
    );

    if (enableGPSSettings) {
      const gpsSpecifications = gpsUtils.setGPSInterval(values);
      // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
      specifications[[SpecificationKeys.GPSSettings.GPS_SEND_INTERVAL]] =
        // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
        gpsSpecifications[[SpecificationKeys.GPSSettings.GPS_SEND_INTERVAL]];
      // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
      specifications[[SpecificationKeys.GPSSettings.GPS_SEND_LATEST_INTERVAL]] =
        gpsSpecifications[
          // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
          [SpecificationKeys.GPSSettings.GPS_SEND_LATEST_INTERVAL]
        ];
    }

    if (productRules.get("needsActivationStatus")) {
      // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
      specifications[[SpecificationKeys.ActivationSettings.SHOULD_BE_ACTIVE]] =
        {
          value: "true",
        };
      // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
      specifications[[SpecificationKeys.ActivationSettings.DEVICE_ACTIVE]] = {
        value: "false",
      };
    }

    if (isEndress && values.orderCode) {
      const orderCodeSpecValue = `${values.product.code}-${values.orderCode}`;
      // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
      specifications[[SpecificationKeys.GeneralSettings.ORDER_CODE]] = {
        value: orderCodeSpecValue,
      };
    }

    // Add additional specifications for FWR30v10 device
    if (productNameForRule === "FWR30v10" || productNameForRule === "UXTR01") {
      const fwr30v10Specifications = productRules.get("specificationDefaults");
      Object.entries(fwr30v10Specifications).forEach(([key, config]) => {
        specifications[key] = config as {
          value: string;
          unit?: string;
          source_timestamp?: string;
          ui_visible: boolean;
        };
      });
    }

    values.specifications?.forEach((specification: $TSFixMe) => {
      if (specification.key !== "" && specification.key?.name) {
        specifications[convertLabelToSpecificationKey(specification.key.name)] =
          {
            value: specification.value,
            ui_visible: isUserProjectEngineer
              ? !!specification.uiVisible
              : true,
          };
      }
    });

    return specifications;
  };

  const handleOnSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      let { manufacturer, product, tenant } = values;
      const { proceedWithTag } = values;
      const productNameForRule = getProductCodeForRules(
        product.code,
        values.extendedOrderCode,
      );
      const productRules = ProductRules.find(
        manufacturer.name,
        productNameForRule,
        manufacturer.tenantPublic,
      );
      const needsInstrumentation = productRules?.get("needsInstrumentation");
      const setFirstStatusToFailure = productRules?.get(
        "setFirstStatusToFailure",
      );

      // create tenant if needed
      tenant = await createNewTenant(tenant, manufacturer);

      // create manufacturer if needed
      if (manufacturer.new) {
        try {
          manufacturer = await api.post("/companies", {
            name: manufacturer.name,
            tenant: { id: tenant.id },
          });
        } catch (error) {
          if (error instanceof BadRequestError && error.contains("taken")) {
            actions.setErrors({
              manufacturer: intl.formatMessage({
                id: "api.error.manufacturer.taken",
              }),
            });
            return;
          }
          throw error;
        }
      }

      // create product if needed
      product = await createNewProduct(product, manufacturer, tenant, values);

      let payload = {
        serial_number: values.serial_number,
        product: { id: product.id },
        description: values.description,
      };

      if (!manufacturer.tenantPublic) {
        (payload as $TSFixMe).tenant = tenant;
      }

      // in case of connected asset set first status to failure
      if (setFirstStatusToFailure) {
        const ehTenantResponse = await api.get("/tenants", {
          name: "Endress+Hauser",
          public: true,
        });
        const statusResponse = await api.get("/asset/statuses", {
          tenant_id: ehTenantResponse.tenants[0].id,
          code: "failure",
        });
        payload = {
          ...payload,
          // @ts-expect-error TS(2322) FIXME: Type '{ status: { id: any; }; serial_number: any; ... Remove this comment to see the full error message
          status: { id: statusResponse.asset_statuses[0].id },
        };
      }

      if (isNotEmpty(values.production_date)) {
        (payload as $TSFixMe).production_date = values.production_date;
      }

      const response = await api.post("/assets", payload);

      const specifications = setSpecifications(
        productRules,
        values,
        manufacturer,
        productNameForRule,
      );

      if (Object.keys(specifications).length > 0) {
        await api.patch(
          `/assets/${response.id}/specifications`,
          specifications,
        );
      }

      if (
        productRules.get("connectedAsset") &&
        !(productNameForRule === "FWR30v10" || productNameForRule === "UXTR01")
      ) {
        await backend.patch(`/connected_assets/${response.id}`);
      }

      if (params?.id) {
        await api.post(`/assets/${response.id}/nodes`, {
          nodes: [
            {
              id: params.id,
            },
          ],
        });
      }

      notifier.showSuccess(
        intl.formatMessage({ id: "asset_create.success_notification" }),
      );

      const url = getUrlToNavigate(
        proceedWithTag,
        needsInstrumentation,
        response,
      );
      browser.navigateTo(url);
    } catch (error) {
      handleError(error, actions, values);
    } finally {
      actions.setSubmitting(false);
      queryClient.invalidateQueries({ queryKey: ["/subscription"] });
    }
  };

  const showAssetScanButton = rules.application().get("showScanAssetButton");
  const assetScanButton = showAssetScanButton ? (
    <ActionBarButtons>
      <ActionButton
        defaultLabel={intl.formatMessage({ id: "button.scan" })}
        icon="lcm-iot-icon-scan"
        target="/assets/create_scan"
      />
    </ActionBarButtons>
  ) : null;

  return (
    <Container>
      <Row>
        <Column>
          <BackButton />
        </Column>
      </Row>
      <Row>
        <Column>
          <ConnectedAssetSubscriptionClue
            // @ts-expect-error TS(2322) FIXME: Type '{ initialValues: { serial_number: string | u... Remove this comment to see the full error message
            initialValues={initialValues}
            formValues={formValues}
            setSubscriptionValid={setSubscriptionValid}
            quotaExceeded={quotaExceeded}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <ActionBar>
            <Heading
              title={intl.formatMessage({ id: "asset_create.header" })}
            />
            {assetScanButton}
          </ActionBar>
        </Column>
      </Row>
      {askPermission ? (
        <AssetPermissionModal
          // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
          serialNumber={askPermissionSerialNumber}
          // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'companyShap... Remove this comment to see the full error message
          manufacturer={askPermissionManufacturer}
          // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'productShap... Remove this comment to see the full error message
          product={askPermissionProduct}
          onClose={() => {
            setAskPermission(false);
          }}
        />
      ) : null}
      <Row>
        <Column lg="7">
          <AssetForm
            onSubmit={handleOnSubmit}
            initialValues={initialValues}
            onChange={setFormValues}
            canSubmit={subscriptionValid}
          />
        </Column>
      </Row>
    </Container>
  );
}

export default withBackend(withApi(AssetCreate));
